import { Helmet } from "react-helmet-async";
import { useSiteContent } from "@/context/ContentContext";

const trimSlash = (s = "") => s.replace(/\/+$/, "");

export default function Seo({
  title,
  description,
  path = "/",
  noindex = false,
  type = "website",
}) {
  const { seo } = useSiteContent();

  const base =
    trimSlash(seo?.canonical_base || "") ||
    (typeof window !== "undefined" ? window.location.origin : "");
  const url = `${base}${path}`;

  const finalTitle = title || seo?.title;
  const finalDesc = description || seo?.description;
  const keywords = seo?.keywords;
  const gsc = seo?.gsc_verification;

  const ogImageRaw = seo?.og_image || "";
  const ogImage = ogImageRaw
    ? ogImageRaw.startsWith("http")
      ? ogImageRaw
      : `${base}${ogImageRaw}`
    : "";

  const personLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: seo?.author || "Syntix",
    jobTitle: seo?.job_title || "Software Developer & Software Architect",
    url: base || undefined,
    description: finalDesc,
  };

  const websiteLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: finalTitle,
    url: base || undefined,
  };

  return (
    <Helmet>
      <title>{finalTitle}</title>
      {finalDesc ? <meta name="description" content={finalDesc} /> : null}
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <link rel="canonical" href={url} />
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow"}
      />
      {gsc ? <meta name="google-site-verification" content={gsc} /> : null}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalTitle} />
      {finalDesc ? <meta property="og:description" content={finalDesc} /> : null}
      <meta property="og:url" content={url} />
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}
      <meta property="og:locale" content="it_IT" />

      {/* Twitter */}
      <meta name="twitter:card" content={ogImage ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={finalTitle} />
      {finalDesc ? <meta name="twitter:description" content={finalDesc} /> : null}
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}

      {/* Structured data */}
      {!noindex ? (
        <script type="application/ld+json">{JSON.stringify(personLd)}</script>
      ) : null}
      {!noindex ? (
        <script type="application/ld+json">{JSON.stringify(websiteLd)}</script>
      ) : null}
    </Helmet>
  );
}
